import React, { useState, useEffect } from "react";
import {
  FormControl,
  TextField,
  useTheme,
  InputAdornment,
} from "@mui/material";
import EuroIcon from "@mui/icons-material/Euro";
import { InputBaseComponentInterface } from "./types";
import { useTranslation } from "react-i18next";
import TooltipDialogCst from "@components/TooltipDialogCst";
import TooltipInput from "@components/InputCst/TooltipInput";
import { TextFieldProps as MUITextFieldProps } from "@mui/material";

export type EuroTextCstProps = MUITextFieldProps &
  InputBaseComponentInterface & {
    residue?: number;
  };

const EuroTextCst: React.FC<EuroTextCstProps> = (props) => {
  const theme = useTheme();
  const { t } = useTranslation(["input"]);

  const {
    id,
    label,
    value,
    placeholder = t("generic-placeholder") + label,
    onChange,
    customWidth,
    error = false,
    errorMessage = "",
    disabled,
    textTooltip,
    tooltipFile,
    tooltipFileIta,
    tooltipFileEn,
    sx,
    maxValue,
    min,
    max,
    isFilterActive,
    endAdornment,
    minWidth = true,
    residue,
    ...rest
  } = props;

  const [displayValue, setDisplayValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const formatNumber = (num: number) =>
    num.toLocaleString("it-IT", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  useEffect(() => {
    if (isFocused) return;
    if (value === undefined || value === null || value === "") {
      setDisplayValue("");
      return;
    }
    const n = Number(value);
    if (!isNaN(n)) setDisplayValue(formatNumber(n));
    else setDisplayValue("");
  }, [value, isFocused]);

  const unformatDisplay = (s: string) =>
    s ? s.replace(/\./g, "").replace(/[€\s]/g, "").trim() : "";

  const parseToNumber = (s: string) => {
    if (!s) return "";
    const normalized = s.replace(/\./g, "").replace(",", ".");
    const num = parseFloat(normalized);
    return isNaN(num) ? "" : num;
  };

  const handleFocus = () => {
    setIsFocused(true);
    setDisplayValue((prev) => unformatDisplay(prev));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value ?? "";
    raw = raw.replace(/\./g, ",");
    raw = raw.replace(/[^0-9,]/g, "");
    const parts = raw.split(",");
    if (parts.length > 2) raw = parts[0] + "," + parts.slice(1).join("");
    setDisplayValue(raw);

    const num = parseToNumber(raw);
    onChange?.({
      ...e,
      target: { ...e.target, value: num === "" ? "" : num },
    } as any);
  };

  const handleBlur: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    setIsFocused(false);
    const num = parseToNumber(displayValue);
    if (num === "") {
      setDisplayValue("");
      onChange?.({
        ...e,
        target: { ...e.target, value: "" },
      } as any);
      return;
    }
    setDisplayValue(formatNumber(num));
    onChange?.({
      ...e,
      target: { ...e.target, value: num },
    } as any);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") (e.target as HTMLElement).blur();
    if (e.key === "e" || e.key === "E") e.preventDefault();
    if (min !== undefined && Number(min) >= 0 && e.key === "-")
      e.preventDefault();
  };

  return (
    <FormControl
      variant="standard"
      sx={{
        display: "flex",
        alignItems: "center",
        width: customWidth ? customWidth : "auto",
        minWidth: minWidth ? "200px" : "",
        "& .MuiFormLabel-root": {
          maxWidth: "calc(100% - 40px)",
          "&[data-shrink='true']": { maxWidth: "calc(133% - 40px)" },
        },
        "& .MuiInputBase-root fieldset": {
          borderColor: isFilterActive ? theme.palette.text.primary : "",
        },
        ...sx,
      }}
    >
      {textTooltip && (
        <TooltipDialogCst inputDialog title={label} textTooltip={textTooltip} />
      )}
      {tooltipFile && (
        <TooltipInput
          inputDialog
          hrefInput
          hrefIta={tooltipFileIta}
          hrefEn={tooltipFileEn}
          disabled={disabled}
        />
      )}

      <TextField
        {...rest}
        type="text"
        sx={{ width: "100%" }}
        size="small"
        error={error}
        helperText={
          <span
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              margin: 0,
            }}
          >
            {error ? errorMessage : ""}
          </span>
        }
        id={id}
        label={label}
        placeholder={placeholder}
        InputProps={{
          endAdornment: (
            <InputAdornment
              position="end"
              style={{ display: "flex", gap: "4px" }}
            >
              <EuroIcon fontSize="small" />
              {residue !== undefined && (
                <>
                  <span>/</span>
                  <span style={{ fontSize: "0.675rem" }}>
                    {Number(residue).toLocaleString("it-IT", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </>
              )}
            </InputAdornment>
          ),
        }}
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        inputProps={{
          maxLength: maxValue,
          inputMode: "decimal",
          min: min ?? 0,
          max: max,
        }}
      />
    </FormControl>
  );
};

export default EuroTextCst;
